import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GraduationCap, Sparkles, Undo2 } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useToast } from '@/hooks/useToast';
import { relativeTime, dateTime } from '@/lib/format';
import { listAgents } from '@/api/agents.api';
import * as trainingApi from '@/api/training.api';
import type { TrainingExample, TrainingProposal } from '@/api/training.api';

/**
 * Modo entrenamiento: bandeja de ejemplos capturados, propuestas del analizador
 * y el historial de "Aprendizaje aprobado" (con revertir). NADA se aplica solo:
 * cada propuesta se aprueba y aplica una por una, con confirmación que nombra
 * al agente destino.
 */

const ESTADO_EJEMPLO: Record<string, { label: string; variant: 'info' | 'default' | 'success' }> = {
  NUEVO: { label: 'Nuevo', variant: 'info' },
  DESCARTADO: { label: 'Descartado', variant: 'default' },
  ANALIZADO: { label: 'Analizado', variant: 'success' },
};

const ESTADO_PROPUESTA: Record<
  string,
  { label: string; variant: 'warning' | 'success' | 'default' | 'brand' | 'danger' }
> = {
  PENDIENTE: { label: 'Pendiente', variant: 'warning' },
  APROBADA: { label: 'Aprobada', variant: 'brand' },
  RECHAZADA: { label: 'Rechazada', variant: 'default' },
  APLICADA: { label: 'Aplicada', variant: 'success' },
  REVERTIDA: { label: 'Revertida', variant: 'danger' },
};

function EjemploCard({ e }: { e: TrainingExample }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [question, setQuestion] = useState(e.question);
  const [answer, setAnswer] = useState(e.answer);

  const update = useMutation({
    mutationFn: (data: { question?: string; answer?: string; descartar?: boolean }) =>
      trainingApi.updateExample(e.id, data),
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['training'] });
      toast.success('Ejemplo actualizado');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'No se pudo actualizar'),
  });

  const meta = ESTADO_EJEMPLO[e.status] ?? ESTADO_EJEMPLO.NUEVO!;
  return (
    <div className="rounded-lg border border-surface-border p-3 dark:border-slate-700">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
        <Badge variant={meta.variant}>{meta.label}</Badge>
        {e.channel?.name && <span>{e.channel.name}</span>}
        <span>{dateTime(e.capturedAt)}</span>
        {e.trainedByName && <span>entrenó: {e.trainedByName}</span>}
        <span className="flex-1" />
        {e.status === 'NUEVO' && !editing && (
          <>
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              Editar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => update.mutate({ descartar: true })}
              loading={update.isPending}
            >
              Descartar
            </Button>
          </>
        )}
      </div>
      {editing ? (
        <div className="mt-2 space-y-2">
          <textarea
            className="w-full rounded-md border border-surface-border bg-white p-2 text-sm dark:border-slate-700 dark:bg-slate-900"
            rows={2}
            value={question}
            onChange={(ev) => setQuestion(ev.target.value)}
          />
          <textarea
            className="w-full rounded-md border border-surface-border bg-white p-2 text-sm dark:border-slate-700 dark:bg-slate-900"
            rows={3}
            value={answer}
            onChange={(ev) => setAnswer(ev.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={() => update.mutate({ question, answer })}
              loading={update.isPending}
            >
              Guardar
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2 space-y-1.5 text-sm">
          <p className="text-slate-600 dark:text-slate-300">
            <span className="font-medium text-slate-400">Cliente:</span> {e.question}
          </p>
          <p className="text-slate-800 dark:text-slate-100">
            <span className="font-medium text-slate-400">Respuesta:</span> {e.answer}
          </p>
        </div>
      )}
    </div>
  );
}

function PropuestaCard({
  p,
  agentes,
}: {
  p: TrainingProposal;
  agentes: { id: string; name: string }[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [contenido, setContenido] = useState(p.contenido);
  const [agentId, setAgentId] = useState(p.agentId ?? '');
  const [confirmApply, setConfirmApply] = useState(false);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['training'] });
  };

  const decide = useMutation({
    mutationFn: (accion: 'aprobar' | 'rechazar') =>
      trainingApi.decideProposal(p.id, {
        accion,
        contenido: contenido !== p.contenido ? contenido : undefined,
        agentId: agentId || null,
      }),
    onSuccess: (_r, accion) => {
      invalidate();
      toast.success(accion === 'aprobar' ? 'Propuesta aprobada' : 'Propuesta rechazada');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'No se pudo decidir'),
  });

  const apply = useMutation({
    mutationFn: () => trainingApi.applyProposal(p.id),
    onSuccess: () => {
      setConfirmApply(false);
      invalidate();
      toast.success('Aprendizaje aplicado al agente');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'No se pudo aplicar'),
  });

  const meta = ESTADO_PROPUESTA[p.estado] ?? ESTADO_PROPUESTA.PENDIENTE!;
  const agenteNombre =
    agentes.find((a) => a.id === (p.agentId ?? agentId))?.name ?? p.agent?.name ?? 'sin agente';
  const editable = p.estado === 'PENDIENTE';

  return (
    <div className="rounded-lg border border-surface-border p-3 dark:border-slate-700">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={meta.variant}>{meta.label}</Badge>
        <Badge variant="default">{p.tipo}</Badge>
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800 dark:text-slate-100">
          {p.titulo}
        </p>
        <span className="text-xs text-slate-400">{relativeTime(p.createdAt)}</span>
      </div>
      {editable ? (
        <textarea
          className="mt-2 w-full rounded-md border border-surface-border bg-white p-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          rows={3}
          value={contenido}
          onChange={(ev) => setContenido(ev.target.value)}
        />
      ) : (
        <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">
          {p.contenido}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {editable && (
          <>
            <select
              className="rounded-md border border-surface-border bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
              value={agentId}
              onChange={(ev) => setAgentId(ev.target.value)}
            >
              <option value="">Agente destino…</option>
              {agentes.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <span className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => decide.mutate('rechazar')}
              loading={decide.isPending}
            >
              Rechazar
            </Button>
            <Button size="sm" onClick={() => decide.mutate('aprobar')} loading={decide.isPending}>
              Aprobar
            </Button>
          </>
        )}
        {p.estado === 'APROBADA' && (
          <>
            <span className="text-xs text-slate-400">Destino: {agenteNombre}</span>
            <span className="flex-1" />
            <Button size="sm" onClick={() => setConfirmApply(true)}>
              Aplicar a {agenteNombre}
            </Button>
          </>
        )}
      </div>
      <Modal
        open={confirmApply}
        onClose={() => setConfirmApply(false)}
        title="Aplicar aprendizaje"
        description={`Se agrega a la sección "Aprendizaje aprobado" del agente ${agenteNombre}. Su prompt base NO se modifica y puedes revertirlo cuando quieras.`}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmApply(false)}>
              Cancelar
            </Button>
            <Button onClick={() => apply.mutate()} loading={apply.isPending}>
              Sí, aplicar a {agenteNombre}
            </Button>
          </>
        }
      >
        <p className="whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">
          {p.contenido}
        </p>
      </Modal>
    </div>
  );
}

export default function Entrenamiento() {
  const qc = useQueryClient();
  const toast = useToast();
  const [filtroEjemplos, setFiltroEjemplos] = useState<string>('NUEVO');

  const summary = useQuery({ queryKey: ['training', 'summary'], queryFn: trainingApi.getSummary });
  const ejemplos = useQuery({
    queryKey: ['training', 'examples', filtroEjemplos],
    queryFn: () => trainingApi.listExamples(filtroEjemplos ? { status: filtroEjemplos } : {}),
  });
  const propuestas = useQuery({
    queryKey: ['training', 'proposals'],
    queryFn: () => trainingApi.listProposals(),
  });
  const aplicados = useQuery({
    queryKey: ['training', 'learnings'],
    queryFn: trainingApi.listLearnings,
  });
  const agentes = useQuery({ queryKey: ['agents', 'all'], queryFn: () => listAgents({ take: 100 }) });
  const agentList = (agentes.data?.items ?? []).map((a) => ({ id: a.id, name: a.name }));

  const analyze = useMutation({
    mutationFn: trainingApi.analyze,
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['training'] });
      toast.success(
        r.analizados === 0
          ? 'No había ejemplos nuevos para analizar'
          : `Analizados ${r.analizados} ejemplos → ${r.propuestas} propuestas nuevas`,
      );
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'No se pudo analizar'),
  });

  const revert = useMutation({
    mutationFn: (id: string) => trainingApi.revertLearning(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['training'] });
      toast.success('Aprendizaje revertido (salió del prompt)');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'No se pudo revertir'),
  });

  const s = summary.data;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Entrenamiento"
        description="Ejemplos capturados atendiendo en modo entrenamiento, propuestas del analizador y lo aprobado. Nada se aplica solo."
        action={
          <Button
            onClick={() => analyze.mutate()}
            loading={analyze.isPending}
            disabled={!s?.vpsConfigurado}
            title={
              s?.vpsConfigurado
                ? 'Analizar los ejemplos nuevos con el motor CLI (puente VPS)'
                : 'Configura el puente VPS para analizar'
            }
          >
            <Sparkles className="h-4 w-4" /> Analizar aprendizaje
            {s ? ` (${s.nuevos})` : ''}
          </Button>
        }
      />

      {s && !s.vpsConfigurado && (
        <Card>
          <CardBody className="flex items-center gap-3 text-sm text-amber-700 dark:text-amber-300">
            <GraduationCap className="h-5 w-5 shrink-0" />
            La captura de ejemplos funciona igual, pero para generar propuestas hay que configurar
            el puente VPS (VPS_AGENT_URL y VPS_AGENT_TOKEN). Los {s.nuevos} ejemplos nuevos quedan
            en cola.
          </CardBody>
        </Card>
      )}

      <Tabs defaultValue="ejemplos">
        <TabsList>
          <TabsTrigger value="ejemplos">Ejemplos {s ? `(${s.nuevos} nuevos)` : ''}</TabsTrigger>
          <TabsTrigger value="propuestas">
            Propuestas {s ? `(${s.pendientes} pendientes)` : ''}
          </TabsTrigger>
          <TabsTrigger value="aplicado">Aplicado {s ? `(${s.aplicados})` : ''}</TabsTrigger>
        </TabsList>

        <TabsContent value="ejemplos">
          <div className="mb-3 flex gap-2">
            {['NUEVO', 'ANALIZADO', 'DESCARTADO', ''].map((st) => (
              <button
                key={st || 'todos'}
                type="button"
                onClick={() => setFiltroEjemplos(st)}
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  filtroEjemplos === st
                    ? 'bg-brand-500 text-white'
                    : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                }`}
              >
                {st === '' ? 'Todos' : (ESTADO_EJEMPLO[st]?.label ?? st)}
              </button>
            ))}
          </div>
          {ejemplos.isLoading ? (
            <Spinner size={24} />
          ) : (ejemplos.data ?? []).length === 0 ? (
            <EmptyState
              icon={GraduationCap}
              title="Sin ejemplos"
              description="Activá el modo entrenamiento en una conversación y respondes tú: cada respuesta se captura como ejemplo."
            />
          ) : (
            <div className="space-y-2">
              {(ejemplos.data ?? []).map((e) => (
                <EjemploCard key={e.id} e={e} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="propuestas">
          {propuestas.isLoading ? (
            <Spinner size={24} />
          ) : (propuestas.data ?? []).length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title="Sin propuestas"
              description="Corré 'Analizar aprendizaje' cuando haya ejemplos nuevos."
            />
          ) : (
            <div className="space-y-2">
              {(propuestas.data ?? []).map((p) => (
                <PropuestaCard key={p.id} p={p} agentes={agentList} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="aplicado">
          {aplicados.isLoading ? (
            <Spinner size={24} />
          ) : (aplicados.data ?? []).length === 0 ? (
            <EmptyState
              icon={GraduationCap}
              title="Nada aplicado todavía"
              description="Lo que apruebes y apliques aparece acá, con su historial y botón de revertir."
            />
          ) : (
            <div className="space-y-2">
              {(aplicados.data ?? []).map((l) => (
                <div
                  key={l.id}
                  className="rounded-lg border border-surface-border p-3 dark:border-slate-700"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={l.activo ? 'success' : 'default'}>
                      {l.activo ? 'Activo en el prompt' : 'Revertido'}
                    </Badge>
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {l.agent?.name ?? l.agentId}
                    </span>
                    <span className="text-xs text-slate-400">
                      aplicado {relativeTime(l.appliedAt)}
                      {l.appliedByName ? ` por ${l.appliedByName}` : ''}
                      {l.revertedAt ? ` · revertido ${relativeTime(l.revertedAt)}` : ''}
                    </span>
                    <span className="flex-1" />
                    {l.activo && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => revert.mutate(l.id)}
                        loading={revert.isPending}
                      >
                        <Undo2 className="h-4 w-4" /> Revertir
                      </Button>
                    )}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">
                    {l.contenido}
                  </p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
